import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const PAGE_PARAM = "page";
export const SIZE_PARAM = "size";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * Server-side pagination synced to the URL query string. rowCount stays in
 * local state (it comes from the server response, not the URL).
 *
 * `resetParams` is the list of param names a filter should clear when it
 * changes (so any filter change returns to the first page). Pass it into the
 * filter hooks: useSearchFilter({ resetParams }), useDateRangeFilter({ resetParams }).
 *
 * @param {Object} [options]
 * @param {number} [options.pageSize=10] - default page size when absent from URL.
 * @param {number[]} [options.pageSizeOptions=[10, 20, 50]] - selectable sizes.
 * @returns {{
 *   paginationModel: { page: number, pageSize: number },
 *   rowCount: number,
 *   setRowCount: Function,
 *   paginationParams: { page: number, size: number }, // spread into request params
 *   dataGridPaginationProps: Object,                  // spread into <DataGrid />
 *   resetParams: string[],                            // pass into filter hooks
 * }}
 */
export const useServerPagination = ({
  pageSize: defaultPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowCount, setRowCount] = useState(0);

  const page = Number(searchParams.get(PAGE_PARAM)) || 0;
  const pageSize = Number(searchParams.get(SIZE_PARAM)) || defaultPageSize;

  const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);

  const onPaginationModelChange = useCallback(
    (model) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        // Omit defaults to keep the URL clean (page 0 / default size).
        if (model.page) params.set(PAGE_PARAM, String(model.page));
        else params.delete(PAGE_PARAM);
        if (model.pageSize && model.pageSize !== defaultPageSize) {
          params.set(SIZE_PARAM, String(model.pageSize));
        } else {
          params.delete(SIZE_PARAM);
        }
        return params;
      });
    },
    [setSearchParams, defaultPageSize],
  );

  // Memoized so it stays referentially stable and can be used directly in a
  // consumer's useCallback/useEffect dependency array without re-fetch loops.
  const paginationParams = useMemo(
    () => ({ page, size: pageSize }),
    [page, pageSize],
  );

  // Stable reference so filter hooks that take it as a dependency stay stable.
  const resetParams = useMemo(() => [PAGE_PARAM], []);

  const dataGridPaginationProps = {
    paginationMode: "server",
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
  };

  return {
    paginationModel,
    rowCount,
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  };
};
